import { guid } from '@/mixins/guid';
import { ASSETS } from '@/constants';

// these configs are used to dynamically populate the data tabs. each type here
// need to have one of the following keys (but cannot have both) image or thumbnail,
// and one of the following: title, name. This is for certain droplets (like group item)
// that could potentially show a feed of any one of these site types
const types = {
  restaurant() {
    return {
      itemListLabel: 'Food Items',
      itemLabel: 'Item',
      tabs: ['Details', 'Food Data', 'Categories'],
      table: ['image', 'name', 'status', 'created'],
      newAddVal: 'name',
      page: {
        modal: true,
        description: 'description',
        metaImage: 'image',
        title: 'name',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-plate.svg`,
          input: 'upload',
          tab: 'Details',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        name: {
          value: false,
          input: 'text',
          tab: 'Details',
          required: true,
        },
        description: {
          value: false,
          input: 'textarea',
          tab: 'Details',
        },
        calories: {
          value: 1200,
          input: 'text',
          tab: 'Food Data',
        },
        price: {
          value: '$1',
          input: 'text',
          tab: 'Food Data',
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
      },
    };
  },
  blog() {
    return {
      itemListLabel: 'Blog Posts',
      itemLabel: 'Post',
      tabs: ['Meta', 'Post', 'Categories'],
      table: ['image', 'title', 'status', 'created'],
      newAddVal: 'title',
      page: {
        modal: false,
        description: 'plainText',
        descriptionPrefixes: [
          'author',
          'publishedOn',
        ],
        metaImage: 'image',
        title: 'title',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-blog.svg`,
          input: 'upload',
          tab: 'Meta',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        title: {
          value: false,
          input: 'text',
          tab: 'Meta',
          required: true,
        },
        author: {
          value: false,
          input: 'text',
          tab: 'Post',
        },
        post: {
          value: false,
          input: 'wysiwyg',
          tab: 'Post',
        },
        likeButton: {
          value: false,
          input: 'checkbox',
          tab: 'Post',
          tooltip: 'This uses the Facebook likes plugin.',
        },
        comments: {
          value: false,
          input: 'checkbox',
          tab: 'Post',
          tooltip: 'This uses the Facebook comments plugin.',
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
      },
    };
  },
  serviceCompany() {
    return {
      itemListLabel: 'Projects',
      itemLabel: 'Project',
      tabs: ['Meta', 'Details', 'Gallery', 'Categories'],
      table: ['image', 'title', 'status', 'created'],
      newAddVal: 'title',
      sort: 'projectDate',
      page: {
        modal: false,
        description: 'plainText',
        descriptionPrefixes: [
          'projectDate.date',
        ],
        metaImage: 'image',
        title: 'title',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-project.svg`,
          input: 'upload',
          tab: 'Meta',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        title: {
          value: false,
          input: 'text',
          tab: 'Meta',
          required: true,
        },
        projectDate: {
          value: {
            date: false,
            month: false,
            day: false,
            year: false,
          },
          input: 'datepicker',
          tab: 'Details',
        },
        summary: {
          value: false,
          input: 'wysiwyg',
          tab: 'Details',
        },
        gallery: {
          value: [],
          input: 'gallery',
          tab: 'Gallery',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
      },
    };
  },
  media() {
    return {
      itemListLabel: 'Media',
      itemLabel: 'Media',
      tabs: ['Meta', 'Media', 'Details', 'Categories', 'Groups'],
      table: ['thumbnail', 'title', 'status', 'created'],
      newAddVal: 'title',
      sort: 'released',
      page: {
        modal: false,
        external: true,
        description: 'plainText',
        descriptionPrefixes: [
          'author',
          'released.date',
        ],
        metaImage: 'thumbnail',
        title: 'title',
      },
      itemData: {
        thumbnail: {
          value: `${ASSETS}mock-images/default-images/mock-media.svg`,
          input: 'upload',
          tab: 'Meta',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        media: {
          value: false,
          tooltip: 'The following audio and video files are accepted: mp3, wmv, mp4, mov, mpeg, mpg.',
          input: 'media',
          tab: 'Media',
        },
        externalMedia: {
          value: {
            youtube: false,
            vimeo: false,
            twitch: false,
            facebook: false,
            soundcloud: false,
            spotify: false,
            other: false,
          },
          hint: '(optional)',
          input: 'externalmedia',
          tab: 'Media',
        },
        title: {
          value: false,
          input: 'text',
          tab: 'Meta',
          required: true,
        },
        author: {
          value: false,
          tooltip: 'This could be the speaker, author or anyone you would want to highlight. This field is not required.',
          input: 'text',
          tab: 'Details',
        },
        released: {
          value: {
            date: false,
            month: false,
            day: false,
            year: false,
          },
          input: 'datepicker',
          tab: 'Details',
        },
        summary: {
          value: false,
          input: 'wysiwyg',
          tab: 'Details',
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
        groups: {
          value: [],
          input: 'multiselect',
          tab: 'Groups',
        },
      },
    };
  },
  // events() {
  //   return {
  //     itemListLabel: 'Events',
  //     itemLabel: 'Event',
  //     tabs: ['Meta', 'Media', 'Details', 'Categories', 'Groups'],
  //     table: ['image', 'title', 'status', 'eventDate'],
  //     newAddVal: 'title',
  //     sort: 'eventDate',
  //     page: {
  //       modal: false,
  //       description: 'plainText',
  //       descriptionPrefixes: [
  //         'title',
  //         'eventDate.date',
  //       ],
  //       metaImage: 'image',
  //       title: 'title',
  //     },
  //     itemData: {
  //       image: {
  //         value: `${ASSETS}mock-images/default-images/mock-event.svg`,
  //         input: 'upload',
  //         tab: 'Meta',
  //         dimensions: {
  //           height: 600,
  //           width: 600,
  //           ratio: 1 / 1,
  //           display: '1:1',
  //         },
  //       },
  //       title: {
  //         value: false,
  //         input: 'text',
  //         tab: 'Meta',
  //         required: true,
  //       },
  //       eventDate: {
  //         value: {
  //           date: false,
  //           month: false,
  //           day: false,
  //           year: false,
  //         },
  //         input: 'datepicker',
  //         tab: 'Details',
  //       },
  //       description: {
  //         value: false,
  //         input: 'wysiwyg',
  //         tab: 'Details',
  //       },
  //       imageGallery: {
  //         value: [],
  //         input: 'gallery',
  //         tab: 'Media',
  //       },
  //       categories: {
  //         value: [],
  //         input: 'multiselect',
  //         tab: 'Categories',
  //       },
  //       groups: {
  //         value: [],
  //         input: 'multiselect',
  //         tab: 'Groups',
  //       },
  //     },
  //   };
  // },
  profiles() {
    return {
      itemListLabel: 'Profiles',
      itemLabel: 'Profile',
      tabs: ['Meta', 'Contact', 'Extras', 'Categories', 'Groups'],
      table: ['image', 'name', 'status', 'created'],
      newAddVal: 'name',
      page: {
        modal: false,
        description: 'plainText',
        metaImage: 'image',
        title: 'name',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-profile.svg`,
          input: 'upload',
          tab: 'Meta',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        name: {
          value: false,
          input: 'text',
          tab: 'Meta',
          required: true,
        },
        address: {
          value: {
            value: false,
            lat: 0,
            lng: 0,
          },
          input: 'address',
          tab: 'Contact',
        },
        email: {
          value: false,
          input: 'text',
          tab: 'Contact',
        },
        phone: {
          value: false,
          input: 'text',
          tab: 'Contact',
        },
        website: {
          value: false,
          input: 'text',
          tab: 'Contact',
        },
        socialMedia: {
          value: [
            { name: 'Facebook', link: 'https://www.facebook.com/' },
            { name: 'Twitter', link: 'https://www.twitter.com/' },
            { name: 'Instagram', link: 'https://www.instagram.com/' },
            { name: 'Linkedin', link: 'https://www.linkedin.com/' },
            { name: 'Pinterest', link: 'https://www.pinterest.com/' },
            { name: 'Slack', link: 'https://www.slack.com/' },
            { name: 'YouTube', link: 'https://www.youtube.com/' },
            { name: 'Tumblr', link: 'https://www.tumblr.com/' },
            { name: 'Reddit', link: 'https://www.reddit.com/' },
            { name: 'Dribbble', link: 'https://www.dribbble.com/' },
            { name: 'Vimeo', link: 'https://www.vimeo.com/' },
            { name: 'Behance', link: 'https://www.reddit.net/' },
            { name: 'Snapchat', link: 'https://www.snapchat.com/' },
            { name: 'Twitch', link: 'https://www.twitch.com/' },
            { name: 'Github', link: 'https://www.github.com/' },
          ].map((icon) => { icon._id = guid.methods.guid(); return icon; }),
          input: 'socialmedia',
          tab: 'Extras',
        },
        mapPin: {
          value: `${ASSETS}mock-images/icons/maps-default-pin.svg`,
          input: 'upload',
          tab: 'Extras',
          tooltip: 'If this profile has an address this pin will be used in any dynamic droplets with a map tied to this item.',
          dimensions: {
            height: 48,
            width: 48,
            ratio: 1 / 1,
            restrict: true,
            display: '1:1',
          },
        },
        imageGallery: {
          value: [],
          input: 'gallery',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
          tab: 'Extras',
        },
        description: {
          value: false,
          input: 'wysiwyg',
          tab: 'Extras',
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
        groups: {
          value: [],
          input: 'multiselect',
          tab: 'Groups',
        },
      },
    };
  },
  places() {
    return {
      itemListLabel: 'Places',
      itemLabel: 'Place',
      tabs: ['Meta', 'Details', 'Media', 'Categories'],
      table: ['image', 'name', 'status', 'created'],
      newAddVal: 'name',
      page: {
        modal: false,
        description: 'plainText',
        metaImage: 'image',
        title: 'name',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-place.svg`,
          input: 'upload',
          tab: 'Meta',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        name: {
          value: false,
          input: 'text',
          tab: 'Meta',
          required: true,
        },
        address: {
          value: {
            value: false,
            lat: 0,
            lng: 0,
          },
          input: 'address',
          tab: 'Details',
          required: true,
        },
        mapPin: {
          value: `${ASSETS}mock-images/icons/maps-default-pin.svg`,
          input: 'upload',
          tab: 'Details',
          dimensions: {
            height: 48,
            width: 48,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        description: {
          value: false,
          input: 'wysiwyg',
          tab: 'Details',
        },
        imageGallery: {
          value: [],
          input: 'gallery',
          tab: 'Media',
          dimensions: {
            height: 600,
            width: 600,
            ratio: 1 / 1,
            display: '1:1',
          },
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
      },
    };
  },
  products() {
    return {
      itemListLabel: 'Products',
      itemLabel: 'Product',
      tabs: ['Details', 'Pricing', 'Limits', 'Media', 'Shipping', 'Options', 'Categories'],
      table: ['image', 'name', 'status', 'totalBought', 'created'],
      newAddVal: 'name',
      page: {
        modal: false,
        description: 'description',
        metaImage: 'image',
        title: 'name',
      },
      itemData: {
        image: {
          value: `${ASSETS}mock-images/default-images/mock-product.svg`,
          input: 'upload',
          tab: 'Details',
          dimensions: {
            height: 750,
            width: 600,
            ratio: 4 / 5,
            display: '4:5',
          },
        },
        name: {
          value: false,
          input: 'text',
          tab: 'Details',
          required: true,
        },
        sku: {
          value: false,
          input: 'text',
          tab: 'Details',
          random: true,
        },
        description: {
          value: false,
          input: 'textarea',
          tab: 'Details',
        },
        price: {
          value: 1,
          input: 'text',
          regex: {
            value: /^[0-9]+(\.[0-9][0-9])?$/,
            message: 'Dollars must be formatted correctly. Example: 0 or 0.00.',
          },
          tab: 'Pricing',
          required: true,
        },
        // discount: {
        //   value: 0,
        //   input: 'text',
        //   tooltip: 'Percentage amount off of price. Good for placing items on sale.',
        //   replace: {
        //     input: /\D/,
        //   },
        //   tab: 'Pricing',
        //   required: false,
        // },
        isTaxable: {
          value: true,
          input: 'checkbox',
          tab: 'Pricing',
        },
        taxIncluded: {
          value: false,
          input: 'checkbox',
          tooltip: 'Select this if you want to manually include your taxes in your product price.',
          tab: 'Pricing',
        },
        productInventory: {
          value: 5,
          input: 'customLimits',
          tab: 'Limits',
          refresh: true,
          tooltip: 'This would be the amount of the product you have in stock. Enter -1 if there is no limit. If there are limits, this value will dynamically change as customers purchase this product.',
          required: true,
          limits: {
            min: -1,
          },
        },
        maxPurchaseQuantity: {
          value: 5,
          input: 'number',
          tab: 'Limits',
          tooltip: 'Use this attribute if you want to set a maximum allowed quantity of your product that a shopper can buy.',
          required: true,
          limits: {
            min: -1,
          },
          grid: {
            colStart: 1,
            colEnd: 3,
            rowStart: 2,
            rowEnd: 3,
          },
        },
        minPurchaseQuantity: {
          value: 1,
          input: 'number',
          tab: 'Limits',
          tooltip: 'Use this attribute if you want to set a minimum allowed quantity of your product that a shopper can buy.',
          required: true,
          limits: {
            min: -1,
          },
          grid: {
            colStart: 3,
            colEnd: 5,
            rowStart: 2,
            rowEnd: 3,
          },
        },
        countStep: {
          value: 1,
          input: 'number',
          tab: 'Limits',
          tooltip: 'By default, the quantity of a product will increment by 1 when a shopper uses the + or - buttons. Use this attribute if you want to override that value.',
          required: true,
          limits: {
            min: -1,
          },
          grid: {
            colStart: 1,
            colEnd: 3,
            rowStart: 3,
            rowEnd: 4,
          },
        },
        extraImages: {
          value: [],
          input: 'gallery',
          tab: 'Media',
          dimensions: {
            height: 750,
            width: 600,
            ratio: 4 / 5,
            display: '4:5',
          },
        },
        shippable: {
          value: true,
          input: 'checkbox',
          tab: 'Shipping',
        },
        weight: {
          value: 0,
          input: 'number',
          tab: 'Shipping',
          hint: '(ounces)',
          required: true,
          limits: {
            min: 0,
          },
          grid: {
            colStart: 1,
            colEnd: 3,
            rowStart: 2,
            rowEnd: 3,
          },
        },
        length: {
          value: 0,
          input: 'number',
          tab: 'Shipping',
          hint: '(inches)',
          required: true,
          limits: {
            min: 0,
          },
          grid: {
            colStart: 3,
            colEnd: 5,
            rowStart: 2,
            rowEnd: 3,
          },
        },
        width: {
          value: 0,
          input: 'number',
          tab: 'Shipping',
          hint: '(inches)',
          required: true,
          limits: {
            min: 0,
          },
          grid: {
            colStart: 1,
            colEnd: 3,
            rowStart: 3,
            rowEnd: 4,
          },
        },
        height: {
          value: 0,
          input: 'number',
          tab: 'Shipping',
          hint: '(inches)',
          required: true,
          limits: {
            min: 0,
          },
          grid: {
            colStart: 3,
            colEnd: 5,
            rowStart: 3,
            rowEnd: 4,
          },
        },
        options: {
          value: [],
          tooltip: 'These options are shown in the visitor\'s shopping cart. They may come in handy if your product has color or size options for example.',
          input: 'customOptions',
          tab: 'Options',
        },
        categories: {
          value: [],
          input: 'multiselect',
          tab: 'Categories',
        },
      },
    };
  },
};

export default types;

export function getDynamicData(params) {
  const shape = types[params.type];
  return shape ? shape(params) : {
    itemLabel: 'Item',
    itemListLabel: 'Item List',
    tabs: ['details'],
    table: ['created'],
    newAddVal: 'name',
    itemData: {},
  };
}



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/item-types.js